import React, {useEffect, useState} from 'react';
import {PlusCircleIcon, UploadIcon, XCircleIcon} from "lucide-react";
import {FileUpload, SelectOption} from "../api/ApiTypes.ts";
import {SamSelect} from "./SamSelect.tsx";
import SamFileSelector from "./SamFileSelector.tsx";
import SamButton from "./SamButton.tsx";
import {useConstantsApi, useFileApi} from "../context/ApiContext.tsx";
import CancelButton from "./CancelButton.tsx";


interface ValidationErrors {
    fileType?: string;
    file?: string;
}

interface MultiFileUploadProps {
    forAppraiser?: number | null;
    onSuccess: () => void;
    onCancel: () => void;
    forOrder: number | null;
    requiresEnv: boolean;
}

export const MultiFileUploadContentFromAppraiser: React.FC<MultiFileUploadProps> = ({
    forAppraiser,
    onSuccess,
    onCancel,
    forOrder,
    requiresEnv
}: MultiFileUploadProps) => {

    const constantsApi = useConstantsApi();
    const fileApi = useFileApi();

    const [fileItems, setFileItems] = useState<FileUpload[]>([
        { file: null, fileType: null }
    ]);

    const [errors, setErrors] = useState<{ [key: number]: ValidationErrors }>({});
    const [fileTypes, setFileTypes] = useState<SelectOption[]>([]);
    const [isUploading, setIsUploading] = useState<boolean>(false);

    useEffect(() => {
        if (forOrder === null) {
            constantsApi.getAppraiserAvailableFileTypes()
                .then(setFileTypes)
                .catch(console.log);
        }
        else {
            constantsApi.getOrderAvailableFileTypes(forOrder)
                .then(setFileTypes)
                .catch(error => {
                    console.log(error);
                    setFileTypes([]);
                });
        }
    }, [forOrder]);


    const handleFileChange = (index: number, file: File | null) => {
        const newItems = [...fileItems];
        newItems[index].file = file;
        setFileItems(newItems);

        // Clear error when file is selected
        if (file && errors[index]?.file) {
            const newErrors = { ...errors };
            delete newErrors[index].file;
            if (Object.keys(newErrors[index]).length === 0) {
                delete newErrors[index];
            }
            setErrors(newErrors);
        }
    };

    const handleFileTypeChange = (index: number, fileType: string | null) => {
        const newItems = [...fileItems];
        newItems[index].fileType = fileType;
        setFileItems(newItems);

        // Clear error when type is selected
        if (fileType && errors[index]?.fileType) {
            const newErrors = { ...errors };
            delete newErrors[index].fileType;
            if (Object.keys(newErrors[index]).length === 0) {
                delete newErrors[index];
            }
            setErrors(newErrors);
        }
    };

    const addFileItem = () => {
        setFileItems([...fileItems, { file: null, fileType: null}]);
    };

    const removeFileItem = (index: number) => {
        if (fileItems.length === 1) {
            // Reset instead of removing if it's the last item
            setFileItems([{ file: null, fileType: null }]);
            return;
        }

        const newItems = fileItems.filter((_, i) => i !== index);

        // Update errors object to remove index and shift higher indices down
        const newErrors: { [key: number]: ValidationErrors } = {};
        Object.keys(errors).forEach(key => {
            const numKey = parseInt(key);
            if (numKey < index) {
                newErrors[numKey] = errors[numKey];
            } else if (numKey > index) {
                newErrors[numKey - 1] = errors[numKey];
            }
        });

        setErrors(newErrors);
        setFileItems(newItems);
    };

    const validateForm = (): boolean => {
        const newErrors: { [key: number]: ValidationErrors } = {};
        let hasErrors = false;

        fileItems.forEach((item, index) => {
            const itemErrors: ValidationErrors = {};

            if (!item.fileType) {
                itemErrors.fileType = 'Please select a file type.';
                hasErrors = true;
            }

            if (!item.file) {
                itemErrors.file = 'Please choose a file to upload.';
                hasErrors = true;
            }

            if (Object.keys(itemErrors).length > 0) {
                newErrors[index] = itemErrors;
            }
        });

        setErrors(newErrors);

        return !hasErrors;
    };

    const uploadFiles = async () => {
        try {
            setIsUploading(true);

            // Create a FormData object
            const formData = new FormData();

            // Add each file with its type and orderId
            fileItems.forEach((item) => {
                if (item.file && item.fileType) {
                    formData.append(`files`, item.file);
                    formData.append(`fileTypes`, item.fileType);
                }
            });

            //if theres an order id, add that bad boy!
            if (forOrder) {
                formData.append('orderId', forOrder.toString());
            }

            if (forAppraiser) {
                formData.append('appraiserId', forAppraiser.toString());
            }
            // Upload the files
            await fileApi.uploadFiles(formData);

            // Call onSuccess to notify parent component
            onSuccess();
        } catch (error) {
            console.error("Error uploading files:", error);
        } finally {
            setIsUploading(false);
        }
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (validateForm()) {
            uploadFiles();
        }
    };

    const handleCancel = () => {
        // Prevent cancellation during upload
        if (!isUploading) {
            onCancel();
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <p className="text-red-500 text-sm mt-1" hidden={!requiresEnv} >*THIS ORDER REQUIRES AN ENV FILE.</p>
            {fileItems.map((item, index) => (
                <div key={index} className={`file-item p-4 ${index > 0 ? 'mt-4 border-t border-gray-200 pt-4' : ''}`}>
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-gray-700">File #{index + 1}</h4>
                        {fileItems.length > 1 && (
                            <button
                                type="button"
                                onClick={() => removeFileItem(index)}
                                className="text-red-500 hover:text-red-700"
                                disabled={isUploading}
                            >
                                <XCircleIcon className="h-5 w-5" />
                            </button>
                        )}
                    </div>

                    <div className="flex items-start gap-4">
                        <div className="flex-1">
                            <SamSelect
                                onChange={(value) => handleFileTypeChange(index, value)}
                                options={fileTypes}
                                value={item.fileType}
                                className="mt-0"
                                placeholder="Select file type"
                                disabled={isUploading}
                            />
                            {errors[index]?.fileType && (
                                <p className="text-red-500 text-sm mt-1">{errors[index].fileType}</p>
                            )}
                        </div>

                        <div className="flex-1">
                            <SamFileSelector
                                name={`uploadFile-${index}`}
                                value={item.file}
                                onChange={(file) => handleFileChange(index, file)}
                                className="mt-1.5"
                                helpText=""
                                disabled={isUploading}
                            />
                            {errors[index]?.file && (
                                <p className="text-red-500 text-sm mt-1">{errors[index].file}</p>
                            )}
                        </div>
                    </div>
                </div>
            ))}


            <div className="mt-4">
                <button
                    type="button"
                    onClick={addFileItem}
                    className="inline-flex items-center px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800"
                    disabled={isUploading}
                >
                    <PlusCircleIcon className="h-4 w-4 mr-1" />
                    Add Another File
                </button>
            </div>

            <div className="flex justify-end gap-2 mt-4">
                <CancelButton
                    type="button"
                    onClick={handleCancel}
                    disabled={isUploading}
                >
                    Cancel
                </CancelButton>
                <SamButton type="submit" disabled={isUploading}>
                    <span className="inline-flex items-center space-x-1">
                        {isUploading ? (
                            <>
                                <span className="animate-spin h-4 w-4 border-2 border-t-transparent border-white rounded-full mr-1"></span>
                                <span>Uploading...</span>
                            </>
                        ) : (
                            <>
                                <UploadIcon className="h-4 w-4" />
                                <span>Upload All Files</span>
                            </>
                        )}
                    </span>
                </SamButton>
            </div>
        </form>
    );
};